// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import Login from './components/Login';
import Register from './components/Register';
import Dashboard from './components/Dashboard';
import Navbar from "./components/Navbar";
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute'; 
import PredictionDisplay from './components/PredictionDisplay';
import AssetIntelligence from './components/AssetIntelligence';
import api from './services/api';
import RealTimeData from './components/RealTimeData';
import ErrorBoundary from './components/ErrorBoundary';
import './i18n';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { useThemeMode } from './theme/ThemeModeContext';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authResolved, setAuthResolved] = useState(false);
  const { i18n } = useTranslation();
  const theme = useTheme();
  const { mode, toggleColorMode } = useThemeMode();

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        const response = await api.get('/user/me');
        if (response.status === 200) {
          setIsAuthenticated(true);
        }
      } catch (err) {
        setIsAuthenticated(false);
        localStorage.removeItem('token');
      } finally {
        setAuthResolved(true);
      }
    };

    const token = localStorage.getItem('token');
    if (token) {
      verifyAuth();
    } else {
      setAuthResolved(true);
    }

    // Load language preference from local storage
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }

    // Save language preference to local storage when it changes
    const handleLanguageChange = (lng) => {
      console.log('Language changed to:', lng);
      localStorage.setItem('language', lng);
    };
    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('current_user');
    setIsAuthenticated(false);
    window.location.href = '/login';
  };

  if (!authResolved) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.default',
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress sx={{ color: '#58C4DD' }} />
          <Typography variant="body2" sx={{ color: 'rgba(223, 232, 241, 0.72)' }}>
            {i18n.t('common.loadingWorkspace')}
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Router>
      <ErrorBoundary>
        <Box
          sx={{
            minHeight: '100vh',
            background:
              theme.palette.mode === 'dark'
                ? 'radial-gradient(circle at top left, rgba(88, 196, 221, 0.1), transparent 18%), radial-gradient(circle at top right, rgba(245, 195, 107, 0.08), transparent 14%), linear-gradient(180deg, #06111D 0%, #081423 100%)'
                : 'radial-gradient(circle at top left, rgba(17, 127, 164, 0.08), transparent 20%), radial-gradient(circle at top right, rgba(224, 154, 45, 0.08), transparent 14%), linear-gradient(180deg, #EEF4FA 0%, #E8F0F8 100%)',
            color: 'text.primary',
          }}
        >
          <Navbar
            isAuthenticated={isAuthenticated}
            handleLogout={handleLogout}
            colorMode={mode}
            toggleColorMode={toggleColorMode}
          />
          <RealTimeData isAuthenticated={isAuthenticated} />
          <Routes>
            <Route
              path="/login"
              element={!isAuthenticated ? <Login setIsAuthenticated={setIsAuthenticated} /> : <Navigate to="/dashboard" />}
            />
            <Route
              path="/register"
              element={!isAuthenticated ? <Register /> : <Navigate to="/dashboard" />}
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/predictions"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <PredictionDisplay />
                </ProtectedRoute>
              }
            />
            <Route
              path="/assets/:symbol"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <AssetIntelligence />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} />} />
          </Routes>
          <Footer />
        </Box>
      </ErrorBoundary>
    </Router>
  );
}

export default App;
